import { __decorate } from "tslib";
import { action, computed, isArrayLike, isObservableArray, isObservableObject, observable, runInAction, toJS } from 'mobx';
import merge from 'lodash/merge';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';
import { getConfig } from 'choerodon-ui/lib/configure';
import DataSet, { addRecordField } from './DataSet';
import { axiosConfigAdapter, checkFieldType, childrenInfoForDelete, findBindField, findBindFields, findBindTargetFields, generateData, generateJSONData, generateResponseData, getChainFieldName, getIf, getRecordValue, getSortedFields, getUniqueKeysAndPrimaryKey, isDirtyRecord, processToJSON, processValue, useCascade, useDirtyField, useNormal, useSelected, } from './utils';
import * as ObjectChainValue from '../_util/ObjectChainValue';
import { BooleanValue, DataSetEvents, DataSetSelection, FieldIgnore, FieldType, RecordStatus } from './enum';
import isSame from '../_util/isSame';
import { treeReduce } from '../_util/treeUtils';
/**
 * 记录ID生成器
 */
const IDGen = (function* (start) {
    while (true) {
        yield ++start;
    }
})(1000);
const EXPANDED_KEY = '__EXPANDED_KEY__'; // TODO:Symbol
const SELECTABLE_KEY = '__SELECTABLE_KEY__'; // TODO:Symbol
const SELECT_KEY = '__SELECT_KEY__'; // TODO:Symbol
const UNSELECT_KEY = '__UNSELECT_KEY__'; // TODO:Symbol
export default class Record {
    constructor(data = {}, dataSet, status = RecordStatus.add) {
        runInAction(() => {
            const initData = isObservableObject(data) ? toJS(data) : data;
            this.fields = observable.map();
            this.status = status;
            this.selectable = true;
            this.isSelected = false;
            this.id = IDGen.next().value;
            this.data = initData;
            if (dataSet) {
                this.dataSet = dataSet;
                const { fields } = dataSet;
                if (fields) {
                    this.initFields(fields);
                }
            }
            this.processData(initData);
        });
    }
    get pristineData() {
        const { dirtyData } = this;
        const data = toJS(this.data);
        if (dirtyData) {
            [...dirtyData.entries()].forEach(([key, value]) => ObjectChainValue.set(data, key, value));
        }
        return data;
    }
    set pristineData(data) {
        runInAction(() => {
            const { dirtyData } = this;
            if (dirtyData) {
                const dirtyKeys = [...dirtyData.keys()];
                const newData = {};
                if (dirtyKeys.length) {
                    dirtyKeys.forEach((key) => {
                        const item = ObjectChainValue.get(this.data, key);
                        ObjectChainValue.set(newData, key, item);
                        const newItem = ObjectChainValue.get(data, key);
                        if (isSame(item, newItem)) {
                            dirtyData.delete(key);
                        }
                        else {
                            dirtyData.set(key, newItem);
                        }
                    });
                }
                this.data = merge({}, data, newData);
            }
            else {
                this.data = data;
            }
        });
    }
    get selectable() {
        return this.getState(SELECTABLE_KEY);
    }
    set selectable(selectable) {
        runInAction(() => {
            if (!selectable) {
                this.isSelected = false;
            }
            this.setState(SELECTABLE_KEY, selectable);
        });
    }
    get isDataSetInAllPageSelection() {
        const { dataSet } = this;
        if (dataSet) {
            return dataSet.isAllPageSelection;
        }
        return false;
    }
    get isSelected() {
        if (this.isDataSetInAllPageSelection) {
            return !this.getState(UNSELECT_KEY);
        }
        return this.getState(SELECT_KEY);
    }
    set isSelected(isSelected) {
        if (this.isDataSetInAllPageSelection) {
            this.setState(UNSELECT_KEY, !isSelected);
        }
        else {
            this.setState(SELECT_KEY, isSelected);
        }
    }
    get key() {
        if (!this.isNew) {
            const { dataSet } = this;
            if (dataSet) {
                const { primaryKey } = dataSet.props;
                if (primaryKey) {
                    const key = this.get(primaryKey);
                    if (isString(key) || isNumber(key)) {
                        return key;
                    }
                }
            }
        }
        return this.id;
    }
    get index() {
        const { dataSet } = this;
        if (dataSet) {
            return dataSet.indexOf(this);
        }
        return -1;
    }
    get indexInParent() {
        const { parent, dataSet } = this;
        if (parent && parent.children) {
            return parent.children.indexOf(this);
        }
        if (dataSet) {
            return dataSet.treeRecords.indexOf(this);
        }
        return -1;
    }
    get isRemoved() {
        return this.status === RecordStatus.delete;
    }
    get isNew() {
        return this.status === RecordStatus.add;
    }
    get isSelectionIndeterminate() {
        const { dataSet } = this;
        if (dataSet && dataSet.selection === DataSetSelection.multiple) {
            const { children } = this;
            if (children) {
                let checkedLength = 0;
                return (children.some(record => {
                    if (record.isSelectionIndeterminate) {
                        return true;
                    }
                    if (record.isSelected) {
                        checkedLength += 1;
                    }
                    return false;
                }) ||
                    (checkedLength > 0 && checkedLength !== children.length));
            }
        }
        return false;
    }
    get isIndeterminate() {
        const { dataSet } = this;
        if (dataSet) {
            const { checkField } = dataSet.props;
            if (checkField) {
                const field = this.getField(checkField);
                const trueValue = field ? field.get(BooleanValue.trueValue) : true;
                const { children } = this;
                if (children) {
                    let checkedLength = 0;
                    return (children.some(record => {
                        if (record.isIndeterminate) {
                            return true;
                        }
                        if (record.get(checkField) === trueValue) {
                            checkedLength += 1;
                        }
                        return false;
                    }) ||
                        (checkedLength > 0 && checkedLength !== children.length));
                }
            }
        }
        return false;
    }
    get isExpanded() {
        const { dataSet } = this;
        if (dataSet) {
            const { expandField } = dataSet.props;
            if (expandField) {
                const expanded = this.get(expandField);
                const field = this.getField(expandField);
                return expanded === (field ? field.get(BooleanValue.trueValue) : true);
            }
        }
        return this.getState(EXPANDED_KEY);
    }
    set isExpanded(expand) {
        const { dataSet } = this;
        if (dataSet) {
            const { expandField } = dataSet.props;
            if (expandField) {
                const field = this.getField(expandField);
                this.set(expandField, field
                    ? expand
                        ? field.get(BooleanValue.trueValue)
                        : field.get(BooleanValue.falseValue)
                    : expand);
            }
            else {
                this.setState(EXPANDED_KEY, expand);
            }
        }
    }
    get previousRecord() {
        const { parent, dataSet } = this;
        let children;
        if (parent) {
            children = parent.children;
        }
        else if (dataSet) {
            children = dataSet.treeData;
        }
        if (children) {
            return children[children.indexOf(this) - 1];
        }
        return undefined;
    }
    get nextRecord() {
        const { parent, dataSet } = this;
        let children;
        if (parent) {
            children = parent.children;
        }
        else if (dataSet) {
            children = dataSet.treeData;
        }
        if (children) {
            return children[children.indexOf(this) + 1];
        }
        return undefined;
    }
    get records() {
        const { dataSet } = this;
        if (dataSet) {
            const { cascadeParent } = this;
            if (cascadeParent && !cascadeParent.isCurrent) {
                return cascadeParent.getCascadeRecordsIncludeDelete(dataSet.parentName) || [];
            }
            return dataSet.records;
        }
        return [];
    }
    get children() {
        const { dataSet } = this;
        if (dataSet) {
            const { parentField, idField, childrenField } = dataSet.props;
            if (childrenField) {
                return this.$children;
            }
            if (parentField && idField) {
                const children = this.records.filter(record => {
                    const childParentId = record.get(parentField);
                    const id = this.get(idField);
                    return !isNil(childParentId) && !isNil(id) && childParentId === id;
                });
                return children.length > 0 ? children : undefined;
            }
        }
        return undefined;
    }
    set children(children) {
        const { dataSet } = this;
        if (dataSet) {
            const { childrenField } = dataSet.props;
            if (childrenField) {
                this.$children = children;
            }
            else {
                throw new Error('Setter of record.children only support in `childrenField` mode.');
            }
        }
    }
    get parent() {
        const { dataSet } = this;
        if (dataSet) {
            const { parentField, idField, childrenField } = dataSet.props;
            if (childrenField) {
                return this.$parent;
            }
            if (parentField && idField) {
                return this.records.find(record => {
                    const parentId = this.get(parentField);
                    const id = record.get(idField);
                    return !isNil(parentId) && !isNil(id) && parentId === id;
                });
            }
        }
        return undefined;
    }
    set parent(parent) {
        const { dataSet } = this;
        if (dataSet) {
            const { childrenField } = dataSet.props;
            if (childrenField) {
                this.$parent = parent;
            }
            else {
                throw new Error('Setter of record.parent only support in `childrenField` mode.');
            }
        }
    }
    get parents() {
        const { parent } = this;
        if (parent) {
            return [parent, ...parent.parents];
        }
        return [];
    }
    get path() {
        return [...this.parents, this];
    }
    get level() {
        const { parent } = this;
        if (parent) {
            return parent.level + 1;
        }
        return 0;
    }
    get dirty() {
        const { status } = this;
        if (status === RecordStatus.update) {
            return true;
        }
        const { dirtyData } = this;
        if (dirtyData && dirtyData.size > 0) {
            return true;
        }
        const { dataSet } = this;
        if (dataSet) {
            const { children } = dataSet;
            return Object.keys(children).some(key => (this.getCascadeRecordsIncludeDelete(key) || []).some(isDirtyRecord));
        }
        return false;
    }
    get cascadeParent() {
        const { dataSet } = this;
        if (dataSet) {
            const { parent, parentName } = dataSet;
            if (parent && parentName) {
                return parent.cascadeRecords.find(record => (record.getCascadeRecordsIncludeDelete(parentName) || []).indexOf(this) !== -1);
            }
        }
        return undefined;
    }
    /**
     * 转换成普通数据
     * 一般用于父级联数据源中的json类型字段
     * 禁止通过此方法获取某一个或几个字段值
     */
    toData(needIgnore, noCascade, isCascadeSelect, all = true) {
        const { status, dataSet, fields } = this;
        const dataToJSON = dataSet && dataSet.dataToJSON;
        const cascade = noCascade === undefined && dataToJSON ? useCascade(dataToJSON) : !noCascade;
        const normal = all || (dataToJSON && useNormal(dataToJSON));
        let dirty = status !== RecordStatus.sync;
        const childrenKeys = cascade && dataSet ? Object.keys(dataSet.children) : undefined;
        const jsonFieldKeys = childrenKeys && [...fields.entries()].reduce((fieldKeys, [key, field]) => {
            if (field.type === FieldType.json && childrenKeys.some(childKey => key === childKey || childKey.startsWith(`${key}.`))) {
                fieldKeys.push(key);
            }
            return fieldKeys;
        }, []);
        const json = this.normalizeData(needIgnore, jsonFieldKeys);
        if (cascade && this.normalizeCascadeData(json, normal, isCascadeSelect)) {
            dirty = true;
        }
        if (jsonFieldKeys) {
            jsonFieldKeys.forEach(key => ObjectChainValue.set(json, key, JSON.stringify(ObjectChainValue.get(json, key)), fields));
        }
        return {
            ...json,
            __dirty: dirty,
        };
    }
    /**
     * 转换成用于提交的数据
     */
    toJSONData(noCascade, isCascadeSelect) {
        const { status } = this;
        return {
            ...this.toData(true, noCascade, isCascadeSelect, false),
            __id: this.id,
            [getConfig('statusKey')]: getConfig('status')[status === RecordStatus.sync ? RecordStatus.update : status],
        };
    }
    validate(all, noCascade) {
        const { dataSet } = this;
        this.validating = true;
        const promises = [];
        if (all || this.status !== RecordStatus.sync) {
            [...this.fields.values()].forEach(field => promises.push(field.checkValidity(false)));
        }
        if (!noCascade && dataSet) {
            const childrenKeys = Object.keys(dataSet.children);
            if (childrenKeys.length) {
                const { dataSetSnapshot, isCurrent } = this;
                childrenKeys.forEach((key) => {
                    const { children } = dataSet;
                    const snapshot = dataSetSnapshot && dataSetSnapshot[key];
                    const ds = children[key];
                    const child = isCurrent ? ds : snapshot && new DataSet().restore(snapshot);
                    if (child) {
                        promises.push(child.validate());
                    }
                    else {
                        const { dataToJSON } = ds;
                        const cascade = noCascade === undefined && dataToJSON ? useCascade(dataToJSON) : !noCascade;
                        ((useSelected(dataToJSON) ? this.getCascadeSelectedRecords(key) : this.getCascadeRecords(key)) || []).forEach(record => promises.push(record.validate(false, !cascade)));
                    }
                });
            }
        }
        return Promise.all(promises)
            .then((results) => {
            const valid = results.every(result => result);
            this.reportValidity(valid);
            delete this.validating;
            return valid;
        }).catch((error) => {
            delete this.validating;
            throw error;
        });
    }
    reportValidity(result) {
        const { dataSet } = this;
        if (dataSet && !dataSet.validating) {
            const prepareForReport = getIf(this, 'prepareForReport', {});
            if (!result) {
                prepareForReport.result = result;
            }
            if (prepareForReport.timeout) {
                window.clearTimeout(prepareForReport.timeout);
            }
            prepareForReport.timeout = window.setTimeout(() => {
                dataSet.reportValidity(prepareForReport.result || true);
                delete this.prepareForReport;
            }, 200);
        }
    }
    ;
    getValidationErrors() {
        return [...this.fields.values()].reduce((results, field) => {
            if (!field.valid) {
                results.push({
                    field,
                    errors: field.getValidationErrorValues(),
                });
            }
            return results;
        }, []);
    }
    getField(fieldName) {
        if (fieldName) {
            return this.fields.get(fieldName);
        }
    }
    getCascadeRecordsIncludeDelete(fieldName) {
        const { dataSet } = this;
        if (fieldName && dataSet) {
            const childDataSet = dataSet.children[fieldName];
            if (childDataSet) {
                if (this.isCurrent) {
                    return childDataSet.records.slice();
                }
                const { dataSetSnapshot } = this;
                const snapshot = dataSetSnapshot && dataSetSnapshot[fieldName];
                if (snapshot) {
                    return snapshot.records;
                }
                const cascadeRecordsMap = getIf(this, 'cascadeRecordsMap', {});
                const cascadeRecords = cascadeRecordsMap[fieldName];
                if (cascadeRecords) {
                    return cascadeRecords;
                }
                const data = this.get(fieldName);
                const cascading = getIf(this, 'cascading', {});
                if (!cascading[fieldName] && isObservableArray(data)) {
                    cascading[fieldName] = true;
                    const records = childDataSet.processData(data, this.isNew ? RecordStatus.add : RecordStatus.sync);
                    delete cascading[fieldName];
                    cascadeRecordsMap[fieldName] = records;
                    return records;
                }
            }
        }
    }
    getCascadeRecords(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => !r.isRemoved);
        }
    }
    getCascadeSelectedRecordsIncludeDelete(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => r.isSelected);
        }
    }
    getCascadeSelectedRecords(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => !r.isRemoved && r.isSelected);
        }
    }
    get(fieldName) {
        return getRecordValue(this, (child, checkField) => child.get(checkField), fieldName);
    }
    set(item, value) {
        if (isString(item)) {
            const oldName = item;
            const fieldName = getChainFieldName(this, oldName);
            const field = this.getField(oldName) || this.getField(fieldName) || findBindField(oldName, fieldName, this) || addRecordField(this, oldName);
            checkFieldType(value, field);
            const oldValue = toJS(this.get(fieldName));
            const newValue = processValue(value, field);
            const newValueForCompare = processToJSON(newValue, field);
            if (!isSame(processToJSON(oldValue, field), newValueForCompare)) {
                const { fields } = this;
                ObjectChainValue.set(this.data, fieldName, newValue, fields);
                const dirtyData = getIf(this, 'dirtyData', () => observable.map());
                if (!(dirtyData.has(fieldName))) {
                    dirtyData.set(fieldName, oldValue);
                    if (this.status === RecordStatus.sync) {
                        this.status = RecordStatus.update;
                    }
                }
                else if (isSame(processToJSON(dirtyData.get(fieldName), field), newValueForCompare)) {
                    dirtyData.delete(fieldName);
                    if (this.status === RecordStatus.update && dirtyData.size === 0 && [...fields.values()].every(f => !f.dirty)) {
                        this.status = RecordStatus.sync;
                    }
                }
                const { dataSet } = this;
                if (dataSet) {
                    dataSet.fireEvent(DataSetEvents.update, {
                        dataSet,
                        record: this,
                        name: oldName,
                        value: newValue,
                        oldValue,
                    });
                    const { checkField } = dataSet.props;
                    if (checkField && fieldName === getChainFieldName(this, checkField)) {
                        const { children } = this;
                        if (children) {
                            children.forEach(record => record.set(fieldName, value));
                        }
                    }
                }
            }
            const { fields } = this;
            [field, ...findBindFields(field, fields, true), ...findBindTargetFields(field, fields, true)].forEach((oneField) => (oneField.checkValidity(false)));
        }
        else if (isPlainObject(item)) {
            Object.keys(item).forEach(key => this.set(key, item[key]));
        }
        return this;
    }
    getPristineValue(fieldName) {
        if (fieldName) {
            const chainFieldName = getChainFieldName(this, fieldName);
            const { dirtyData } = this;
            if (dirtyData && dirtyData.has(chainFieldName)) {
                return dirtyData.get(chainFieldName);
            }
            return this.get(chainFieldName);
        }
    }
    init(item, value) {
        const { fields, data, dirtyData } = this;
        if (isString(item)) {
            const oldName = item;
            const fieldName = getChainFieldName(this, oldName);
            const field = this.getField(oldName) || this.getField(fieldName) || findBindField(oldName, fieldName, this) || addRecordField(this, fieldName);
            const newValue = processValue(value, field);
            if (dirtyData) {
                dirtyData.delete(fieldName);
            }
            ObjectChainValue.set(data, fieldName, newValue, fields);
            field.commit();
        }
        else if (isPlainObject(item)) {
            Object.keys(item).forEach(key => this.init(key, item[key]));
        }
        return this;
    }
    clone() {
        const { dataSet } = this;
        const cloneData = this.toData();
        if (dataSet) {
            const { primaryKey } = dataSet.props;
            if (primaryKey) {
                delete cloneData[primaryKey];
            }
            return new Record(cloneData, dataSet);
        }
        return new Record(cloneData);
    }
    ready() {
        return Promise.resolve(true);
        // return Promise.all([...this.fields.values()].map(field => field.ready()));
    }
    async tls(name) {
        const tlsKey = getConfig('tlsKey');
        const { dataSet } = this;
        if (dataSet && name) {
            const tlsData = this.get(tlsKey) || {};
            if (!(name in tlsData)) {
                const { axios, lang } = dataSet;
                const { primaryKey } = dataSet.props;
                const newConfig = axiosConfigAdapter('tls', dataSet, {}, primaryKey && { key: this.get(primaryKey) }, { name, record: this });
                if (newConfig.url && !this.isNew) {
                    const result = await axios(newConfig);
                    if (result) {
                        const dataKey = getConfig('dataKey');
                        this.commitTls(generateResponseData(result, dataKey)[0], name);
                    }
                }
                else {
                    this.commitTls([...this.fields.entries()].reduce((data, [key, field]) => {
                        if (field.type === FieldType.intl) {
                            data[key] = {
                                [lang]: this.get(key),
                            };
                        }
                        return data;
                    }, {}), name);
                }
            }
        }
    }
    reset() {
        const { status, fields, dataSet, dirty, isRemoved } = this;
        [...fields.values()].forEach(field => field.commit());
        if (status === RecordStatus.update || isRemoved) {
            this.status = RecordStatus.sync;
        }
        if (isRemoved || dirty) {
            this.data = toJS(this.pristineData);
            this.dirtyData = undefined;
            this.memo = undefined;
            if (dataSet && !dataSet.resetInBatch) {
                dataSet.fireEvent(DataSetEvents.reset, { records: [this], dataSet });
            }
        }
        return this;
    }
    save() {
        this.memo = toJS(this.data);
        return this;
    }
    restore() {
        const { memo } = this;
        if (memo) {
            this.set(memo);
            this.memo = undefined;
        }
        return this;
    }
    clear() {
        return this.set([...this.fields.keys()].reduce((obj, key) => {
            obj[key] = null;
            return obj;
        }, {}));
    }
    commit(data, dataSet) {
        if (dataSet) {
            const { records } = this;
            if (this.isRemoved) {
                const index = records.indexOf(this);
                if (index !== -1) {
                    if (dataSet.records === records) {
                        dataSet.totalCount -= 1;
                    }
                    records.splice(index, 1);
                }
                return this;
            }
            if (this.isNew) {
                const index = records.indexOf(this);
                if (index !== -1 && dataSet.records === records) {
                    dataSet.totalCount += 1;
                }
            }
            if (data) {
                this.data = data;
                this.processData(data, true);
                const { children } = dataSet;
                const keys = Object.keys(children);
                if (keys.length) {
                    const { isCurrent } = this;
                    const tmpDs = new DataSet();
                    const dataSetSnapshot = getIf(this, 'dataSetSnapshot', {});
                    keys.forEach(key => {
                        const snapshot = dataSetSnapshot[key];
                        const ds = children[key];
                        const child = isCurrent
                            ? ds
                            : snapshot && tmpDs.restore(snapshot);
                        if (child) {
                            dataSetSnapshot[key] = child.commitData(data[key] || []).snapshot();
                        }
                        else {
                            const cascadeRecords = this.getCascadeRecordsIncludeDelete(key);
                            if (cascadeRecords) {
                                cascadeRecords.forEach(r => r.commit(omit(r.toData(), ['__dirty']), ds));
                            }
                        }
                    });
                }
            }
        }
        this.dirtyData = undefined;
        [...this.fields.values()].forEach(field => field.commit());
        this.status = RecordStatus.sync;
        return this;
    }
    setState(item, value) {
        const state = getIf(this, 'state', () => observable.map());
        if (isString(item)) {
            state.set(item, value);
        }
        else if (isPlainObject(item)) {
            state.merge(item);
        }
        return this;
    }
    getState(key) {
        const { state } = this;
        return state && state.get(key);
    }
    treeReduce(fn, initialValue) {
        return treeReduce([this], fn, initialValue);
    }
    commitTls(data = {}, name) {
        const tlsKey = getConfig('tlsKey');
        if (!(name in data)) {
            data[name] = {};
        }
        Object.keys(data).forEach((key) => {
            const value = data[key];
            const field = this.getField(key);
            if (field) {
                const transformResponse = field.get('transformResponse');
                if (transformResponse) {
                    const originValue = { ...value };
                    Object.keys(value).forEach((language) => {
                        value[language] = transformResponse(value[language], originValue);
                    });
                }
            }
            ObjectChainValue.set(this.data, `${tlsKey}.${key}`, value);
        });
    }
    initFields(fields) {
        [...fields.keys()].forEach((key) => addRecordField(this, key));
    }
    addField(name, fieldProps = {}) {
        const old = this.fields.get(name);
        const field = addRecordField(this, name, fieldProps);
        if (!old) {
            const data = toJS(this.data);
            const newData = { ...data };
            this.processFieldValue(name, field, this.fields, newData, data);
        }
        return field;
    }
    processFieldValue(fieldName, field, fields, newData, data, needMerge) {
        let value = ObjectChainValue.get(newData, fieldName);
        const chainFieldName = getChainFieldName(this, fieldName);
        const transformResponse = field.get('transformResponse');
        if (chainFieldName !== fieldName) {
            const bindValue = ObjectChainValue.get(newData, chainFieldName);
            if (isNil(value) && !isNil(bindValue)) {
                value = bindValue;
            }
        }
        if (transformResponse) {
            value = transformResponse(value, data);
        }
        value = processValue(value, field, !needMerge && this.isNew);
        if (value === null) {
            value = undefined;
        }
        if (needMerge && isObject(value)) {
            const oldValue = ObjectChainValue.get(this.data, chainFieldName);
            if (isObject(oldValue)) {
                value = merge(oldValue, value);
            }
        }
        ObjectChainValue.set(newData, chainFieldName, value, fields);
        ObjectChainValue.set(this.data, chainFieldName, value, fields);
    }
    processData(data = {}, needMerge) {
        const newData = { ...data };
        const { fields } = this;
        getSortedFields(fields).forEach(([fieldName, field]) => this.processFieldValue(fieldName, field, fields, newData, data, needMerge));
    }
    normalizeData(needIgnore, jsonFields) {
        const { fields, dataSet } = this;
        const dataToJSON = dataSet && dataSet.dataToJSON;
        const onlyDirtyField = needIgnore && dataToJSON ? useDirtyField(dataToJSON) : false;
        const neverKeys = onlyDirtyField ? getUniqueKeysAndPrimaryKey(dataSet) : [];
        const json = onlyDirtyField ? pick(this.data, neverKeys) : toJS(this.data);
        const fieldIgnore = onlyDirtyField ? FieldIgnore.clean : undefined;
        const objectFieldsList = [];
        const normalFields = [];
        const ignoreFieldNames = new Set();
        [...fields.entries()].forEach(([key, field]) => {
            if (field && (!jsonFields || !jsonFields.includes(key))) {
                const ignore = field.get('ignore') || fieldIgnore;
                if (needIgnore &&
                    !neverKeys.includes(key) &&
                    (ignore === FieldIgnore.always || (ignore === FieldIgnore.clean && !field.dirty))) {
                    ignoreFieldNames.add(key);
                }
                else {
                    const type = field.get('type');
                    if (type === FieldType.object) {
                        const level = key.split('.').length - 1;
                        objectFieldsList[level] = (objectFieldsList[level] || []).concat(field);
                    }
                    else {
                        normalFields.push(field);
                    }
                }
            }
        });
        [...objectFieldsList, normalFields].forEach(items => {
            if (items) {
                items.forEach(field => {
                    const { name } = field;
                    let value = ObjectChainValue.get(onlyDirtyField ? { ...this.data, ...json } : json, name);
                    const bind = field.get('bind');
                    const transformRequest = field.get('transformRequest');
                    if (bind) {
                        value = this.get(getChainFieldName(this, name));
                    }
                    const old = value;
                    value = processToJSON(value, field);
                    if (transformRequest) {
                        // compatible old logic
                        value = isString(field.get('multiple')) && isArrayLike(old) ?
                            transformRequest(value, this) :
                            processToJSON(transformRequest(old, this), field);
                    }
                    if (value !== undefined) {
                        ObjectChainValue.set(json, name, value, fields);
                    }
                    else {
                        ignoreFieldNames.add(name);
                    }
                });
            }
        });
        [...ignoreFieldNames].forEach(key => ObjectChainValue.remove(json, key));
        return json;
    }
    normalizeCascadeData(json, normal, isSelect) {
        const { dataSetSnapshot, dataSet, fields, isRemoved } = this;
        if (dataSet) {
            let dirty = false;
            const { children } = dataSet;
            if (isRemoved) {
                childrenInfoForDelete(json, children);
            }
            else {
                const keys = Object.keys(children);
                if (keys.length) {
                    const { isCurrent } = this;
                    keys.forEach(name => {
                        const snapshot = dataSetSnapshot && dataSetSnapshot[name];
                        const child = (!isCurrent && snapshot && new DataSet().restore(snapshot)) || children[name];
                        if (child) {
                            const { dataToJSON } = child;
                            const records = this.getCascadeRecordsIncludeDelete(name);
                            const selected = isSelect || useSelected(dataToJSON) ? this.getCascadeSelectedRecordsIncludeDelete(name) : records;
                            const jsonArray = normal || useNormal(dataToJSON)
                                ? records && generateData(records)
                                : selected && generateJSONData(child, selected);
                            if (jsonArray) {
                                if (jsonArray.dirty) {
                                    dirty = true;
                                }
                                ObjectChainValue.set(json, name, jsonArray.data, fields);
                            }
                        }
                    });
                }
            }
            return dirty;
        }
    }
}
__decorate([
    observable
], Record.prototype, "fields", void 0);
__decorate([
    observable
], Record.prototype, "data", void 0);
__decorate([
    observable
], Record.prototype, "dirtyData", void 0);
__decorate([
    computed
], Record.prototype, "pristineData", null);
__decorate([
    observable
], Record.prototype, "status", void 0);
__decorate([
    observable
], Record.prototype, "isCurrent", void 0);
__decorate([
    observable
], Record.prototype, "isCached", void 0);
__decorate([
    observable
], Record.prototype, "editing", void 0);
__decorate([
    observable
], Record.prototype, "pending", void 0);
__decorate([
    observable
], Record.prototype, "childrenLoaded", void 0);
__decorate([
    observable
], Record.prototype, "state", void 0);
__decorate([
    computed
], Record.prototype, "key", null);
__decorate([
    computed
], Record.prototype, "index", null);
__decorate([
    computed
], Record.prototype, "indexInParent", null);
__decorate([
    computed
], Record.prototype, "isSelectionIndeterminate", null);
__decorate([
    computed
], Record.prototype, "isIndeterminate", null);
__decorate([
    computed
], Record.prototype, "isExpanded", null);
__decorate([
    computed
], Record.prototype, "previousRecord", null);
__decorate([
    computed
], Record.prototype, "nextRecord", null);
__decorate([
    computed
], Record.prototype, "records", null);
__decorate([
    computed
], Record.prototype, "children", null);
__decorate([
    computed
], Record.prototype, "parent", null);
__decorate([
    computed
], Record.prototype, "parents", null);
__decorate([
    computed
], Record.prototype, "path", null);
__decorate([
    computed
], Record.prototype, "dirty", null);
__decorate([
    computed
], Record.prototype, "cascadeParent", null);
__decorate([
    action
], Record.prototype, "set", null);
__decorate([
    action
], Record.prototype, "init", null);
__decorate([
    action
], Record.prototype, "tls", null);
__decorate([
    action
], Record.prototype, "reset", null);
__decorate([
    action
], Record.prototype, "save", null);
__decorate([
    action
], Record.prototype, "restore", null);
__decorate([
    action
], Record.prototype, "clear", null);
__decorate([
    action
], Record.prototype, "commit", null);
__decorate([
    action
], Record.prototype, "setState", null);
__decorate([
    action
], Record.prototype, "commitTls", null);
__decorate([
    action
], Record.prototype, "addField", null);
